import { Option } from 'effect'
import { Dollar } from 'libs/dollar'
import { formatK1OptionalDecimal } from 'libs/Number/formatNumber'
import { Color } from 'presentation/libs/chakra/chakra.types'
import { getFeatures, usePlanData } from 'presentation/screens/Billing/shared/usePlanData'
import { useEffect, useState } from 'react'

// Simple ViewModel type for the component
type FeatureKey =
  | 'team-members'
  | 'lead-list-exports'
  | 'comps'
  | 'skiptrace'
  | 'bulk-skiptrace'

type CurrentFeaturesSectionViewModel = {
  featureKey: FeatureKey
  featureNameText: string
  featureValueText: string
  featureValueTextColor: Color
  disclosure: Option.Option<string>
}[]

// Define feature types for TypeScript
type Feature = {
  __typename?: string
}

type TeamFeature = {
  __typename: 'SubscriptionFeatureTeam'
  limits?: { value?: number, interval?: string }[]
} & Feature

type LeadsFeature = {
  __typename: 'SubscriptionFeatureLeads'
  limits?: { interval?: string, value?: number }[]
  usages?: { interval?: string, value?: number }[]
} & Feature

type CompsFeature = {
  __typename: 'SubscriptionFeatureComps'
  limits?: { interval?: string, value?: number }[]
  usages?: { interval?: string, value?: number }[]
  prices?: { price?: number, pricingUnit?: string }[]
} & Feature

type SkiptraceFeature = {
  __typename: 'SubscriptionFeatureSkiptrace'
  limits?: { interval?: string, value?: number }[]
  usages?: { interval?: string, value?: number }[]
  prices?: { price?: number, pricingUnit?: string, included?: { limit?: number, used?: number } }[]
} & Feature

type IndividualSkiptraceFeature = {
  __typename: 'SubscriptionFeatureIndividualSkiptrace'
  limits?: { interval?: string, value?: number }[]
  usages?: { interval?: string, value?: number }[]
  prices?: { price?: number, pricingUnit?: string, included?: { limit?: number, used?: number } }[]
} & Feature

const createSkiptraceViewModelEntry = ({
  feature,
  featureKey,
  featureNameText,
}: {
  feature: SkiptraceFeature | IndividualSkiptraceFeature
  featureKey: Extract<FeatureKey, 'skiptrace' | 'bulk-skiptrace'>
  featureNameText: string
}) => {
  const pricing = feature.prices?.[0]
  const includedLimit = pricing?.included?.limit || 0
  const includedUsed = pricing?.included?.used || 0

  if (includedLimit > 0) {
    const price = pricing?.price || 0
    const priceText = `${formatK1OptionalDecimal(includedUsed)} / ${formatK1OptionalDecimal(includedLimit)} free | ${Dollar.formatPennies(price)}/ea`

    return {
      featureKey,
      featureNameText,
      featureValueText: priceText,
      featureValueTextColor: 'accent.blue-to-gray' as const,
      disclosure: Option.some(String(includedLimit)),
    }
  }

  if (feature.limits && feature.limits.length > 0) {
    const limit = feature.limits[0]?.value || 0
    const used = feature.usages?.[0]?.value || 0
    const price = feature.prices?.[0]?.price || 0

    if (limit > 0) {
      const priceText = `${formatK1OptionalDecimal(used)} / ${formatK1OptionalDecimal(limit)} free | ${Dollar.formatPennies(price)}/ea`

      return {
        featureKey,
        featureNameText,
        featureValueText: priceText,
        featureValueTextColor: 'accent.blue-to-gray' as const,
        disclosure: Option.some(String(limit)),
      }
    }

    return {
      featureKey,
      featureNameText,
      featureValueText: 'Included',
      featureValueTextColor: 'accent.blue-to-gray' as const,
      disclosure: Option.none<string>(),
    }
  }

  return {
    featureKey,
    featureNameText,
    featureValueText: 'Unlimited',
    featureValueTextColor: 'accent.blue-to-gray' as const,
    disclosure: Option.none<string>(),
  }
}

// Simple, straightforward usePresenter implementation
const usePresenter = () => {
  const [viewModel, setViewModel] = useState<CurrentFeaturesSectionViewModel | null>(null)
  const { loading, data, error } = usePlanData()

  useEffect(() => {
    if (!loading) {
      try {
        const features = getFeatures(data) as Feature[]

        // If we have data, process it
        if (features && features.length > 0) {
          const vm: CurrentFeaturesSectionViewModel = []

          // Team Members
          const teamFeature = features.find((f: Feature): f is TeamFeature => f.__typename === 'SubscriptionFeatureTeam')
          if (teamFeature) {
            const limit = teamFeature.limits?.[0]?.value || 0
            vm.push({
              featureKey: 'team-members',
              featureNameText: 'Team Members',
              featureValueText: limit > 0 ? formatK1OptionalDecimal(limit) : 'Unlimited',
              featureValueTextColor: 'accent.blue-to-gray',
              disclosure: Option.none(),
            })
          } else {
            vm.push({
              featureKey: 'team-members',
              featureNameText: 'Team Members',
              featureValueText: 'Zero',
              featureValueTextColor: 'negative.500',
              disclosure: Option.none(),
            })
          }

          // Lead List Exports
          const leadsFeature = features.find((f: Feature): f is LeadsFeature => f.__typename === 'SubscriptionFeatureLeads')
          if (leadsFeature) {
            const limit = leadsFeature.limits?.[0]?.value || 0
            const used = leadsFeature.usages?.[0]?.value || 0
            const text = limit > 0
              ? `${formatK1OptionalDecimal(used)} / ${formatK1OptionalDecimal(limit)}`
              : 'Unlimited'

            vm.push({
              featureKey: 'lead-list-exports',
              featureNameText: 'Lead List Exports',
              featureValueText: text,
              featureValueTextColor: 'accent.blue-to-gray',
              disclosure: Option.none(),
            })
          } else {
            vm.push({
              featureKey: 'lead-list-exports',
              featureNameText: 'Lead List Exports',
              featureValueText: 'Zero',
              featureValueTextColor: 'negative.500',
              disclosure: Option.none(),
            })
          }

          // Comps
          const compsFeature = features.find((f: Feature): f is CompsFeature => f.__typename === 'SubscriptionFeatureComps')
          if (compsFeature) {
            // If limits array is empty, it's unlimited
            if (!compsFeature.limits || compsFeature.limits.length === 0) {
              vm.push({
                featureKey: 'comps',
                featureNameText: 'Comps',
                featureValueText: 'Unlimited',
                featureValueTextColor: 'accent.blue-to-gray',
                disclosure: Option.none(),
              })
            } else {
              const limit = compsFeature.limits?.[0]?.value || 0
              const used = compsFeature.usages?.[0]?.value || 0
              const price = compsFeature.prices?.[0]?.price || 0

              // If limit is present, show quota
              if (limit > 0) {
                const priceText = `${formatK1OptionalDecimal(used)} / ${formatK1OptionalDecimal(limit)} free | ${Dollar.formatPennies(price)}/ea`
                vm.push({
                  featureKey: 'comps',
                  featureNameText: 'Comps',
                  featureValueText: priceText,
                  featureValueTextColor: 'accent.blue-to-gray',
                  disclosure: Option.some(String(limit)),
                })
              } else {
                // For special non-standard plans, show a basic included status
                vm.push({
                  featureKey: 'comps',
                  featureNameText: 'Comps',
                  featureValueText: 'Included',
                  featureValueTextColor: 'accent.blue-to-gray',
                  disclosure: Option.none(),
                })
              }
            }
          } else {
            vm.push({
              featureKey: 'comps',
              featureNameText: 'Comps',
              featureValueText: 'Zero',
              featureValueTextColor: 'negative.500',
              disclosure: Option.none(),
            })
          }

          // Skiptrace
          const skiptraceFeature = features.find((f: Feature): f is SkiptraceFeature => f.__typename === 'SubscriptionFeatureSkiptrace')
          const individualSkiptraceFeature = features.find((f: Feature): f is IndividualSkiptraceFeature => f.__typename === 'SubscriptionFeatureIndividualSkiptrace')
          const hasSplitSkiptraceFeatures = !!skiptraceFeature && !!individualSkiptraceFeature

          if (individualSkiptraceFeature) {
            vm.push(createSkiptraceViewModelEntry({
              feature: individualSkiptraceFeature,
              featureKey: 'skiptrace',
              featureNameText: 'Skiptrace',
            }))
          }

          if (skiptraceFeature) {
            vm.push(createSkiptraceViewModelEntry({
              feature: skiptraceFeature,
              featureKey: 'bulk-skiptrace',
              featureNameText: hasSplitSkiptraceFeatures
                ? 'Bulk Skiptrace'
                : 'Skiptrace',
            }))
          } else if (!individualSkiptraceFeature) {
            vm.push({
              featureKey: 'bulk-skiptrace',
              featureNameText: 'Skiptrace',
              featureValueText: 'Zero',
              featureValueTextColor: 'negative.500',
              disclosure: Option.none(),
            })
          }

          setViewModel(vm)
        } else {
          // Fallback for no features
          setViewModel(createFallbackViewModel())
        }
      } catch (err) {
        // Error handling without console.error
        // If anything fails, use the fallback view
        setViewModel(createFallbackViewModel())
      }
    }
  }, [loading, data, error])

  // Wrap in Option to maintain compatibility with the original component
  return loading ? Option.none() : Option.some(viewModel || [])
}

// Simple fallback for when the API fails or returns unexpected data
const createFallbackViewModel = (): CurrentFeaturesSectionViewModel => [
  {
    featureKey: 'team-members',
    featureNameText: 'Team Members',
    featureValueText: 'Included',
    featureValueTextColor: 'accent.blue-to-gray',
    disclosure: Option.none(),
  },
  {
    featureKey: 'lead-list-exports',
    featureNameText: 'Lead List Exports',
    featureValueText: 'Included',
    featureValueTextColor: 'accent.blue-to-gray',
    disclosure: Option.none(),
  },
  {
    featureKey: 'comps',
    featureNameText: 'Comps',
    featureValueText: 'Included',
    featureValueTextColor: 'accent.blue-to-gray',
    disclosure: Option.none(),
  },
  {
    featureKey: 'bulk-skiptrace',
    featureNameText: 'Skiptrace',
    featureValueText: 'Included',
    featureValueTextColor: 'accent.blue-to-gray',
    disclosure: Option.none(),
  },
]

const CurrentFeaturesSectionViewModel = {
  usePresenter,
}

export default CurrentFeaturesSectionViewModel
